import { useState, useEffect, useCallback } from 'react';

export interface AMEntry {
  id: number;
  name: string;
  filename: string;
}

export interface AMManifest {
  account_managers: AMEntry[];
}

export interface TimesheetRecord {
  year: number;
  month: number;
  account_manager_id: number;
  account_manager: string;
  project_id: number;
  project_name: string;
  project_category: string;
  project_billing: string;
  project_type: string;
  project_manager_id: number | null;
  project_manager: string;
  project_is_active: number;
  resource_id: number;
  resource_username: string;
  resource_name: string;
  resource_is_active: number;
  resource_department: string;
  resource_technology: string;
  resource_grade: string;
  total_hours: string;
  billable_hours: string;
  non_billable_hours: string;
  active_days: number;
  entries_count: number;
  late_entries_count: string;
  pending_signoff_entries: string;
  full_day_leaves: string;
  half_day_leaves: string;
  total_leave_days: string;
}

export interface ProjectClientMapping {
  project_id: number;
  project_name: string;
  client_id: number;
  client_name: string;
}

export interface AccountOwnerOverride {
  projectName: string;
  accountOwnerName: string;
}

export interface ClientInvoiceByPeriod {
  by_year_month: Record<string, { clients: Record<string, number> }>;
  by_year: Record<string, { clients: Record<string, number> }>;
  client_tax_pct?: Record<string, number>;
}

export interface RateConfig {
  default_rate_per_hour: number;
  overrides?: Array<{ year: number; month?: number; rate: number }>;
}

export interface AccountOwnerDataResult {
  records: TimesheetRecord[];
  amManifest: AMEntry[];
  projectToClient: Record<string, { client_id: string; client_name: string }>;
  clientBillingRateByClientId: Record<string, number>;
  gradeTechRateByGradeTech: Record<string, number>;
  clientInvoiceByPeriod: ClientInvoiceByPeriod | null;
  accountOwnerProjectOverrides: AccountOwnerOverride[];
  rateConfig: RateConfig | null;
  /** Global $/h subtracted from grade/tech cost rate (from config/account_owner_misc_cost.json). */
  miscExpensePerHour: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAccountOwnerData(): AccountOwnerDataResult {
  const [records, setRecords] = useState<TimesheetRecord[]>([]);
  const [amManifest, setAmManifest] = useState<AMEntry[]>([]);
  const [projectToClient, setProjectToClient] = useState<Record<string, { client_id: string; client_name: string }>>({});
  const [clientBillingRateByClientId, setClientBillingRateByClientId] = useState<Record<string, number>>({});
  const [gradeTechRateByGradeTech, setGradeTechRateByGradeTech] = useState<Record<string, number>>({});
  const [clientInvoiceByPeriod, setClientInvoiceByPeriod] = useState<ClientInvoiceByPeriod | null>(null);
  const [accountOwnerProjectOverrides, setAccountOwnerProjectOverrides] = useState<AccountOwnerOverride[]>([]);
  const [rateConfig, setRateConfig] = useState<RateConfig | null>(null);
  const [miscExpensePerHour, setMiscExpensePerHour] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener('accountOwnerMiscCostSaved', bump);
    return () => window.removeEventListener('accountOwnerMiscCostSaved', bump);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);

        // 1. Fetch manifest
        const manifestRes = await fetch('/jsonbyAM/am_manifest.json');
        if (!manifestRes.ok) throw new Error('Failed to load AM manifest');
        const manifestData: AMManifest = await manifestRes.json();
        const ams = manifestData.account_managers || [];

        // 2. Fetch all per-AM files in parallel
        const amFiles = await Promise.all(
          ams.map(am => fetch(`/jsonbyAM/${am.filename}`).then(r => r.ok ? r.json() : []))
        );
        const allRecords: TimesheetRecord[] = amFiles.flat();

        // 3. Fetch supporting data in parallel
        const miscPromise = (async (): Promise<number> => {
          try {
            const api = await fetch('/api/account-owner-misc-cost');
            if (api.ok) {
              const j = (await api.json()) as { misc_expense_per_hour?: unknown };
              const v = Number(j.misc_expense_per_hour);
              return Number.isFinite(v) && v >= 0 ? v : 0;
            }
          } catch {
            /* ignore */
          }
          try {
            const r = await fetch('/config/account_owner_misc_cost.json');
            if (r.ok) {
              const j = (await r.json()) as { misc_expense_per_hour?: unknown };
              const v = Number(j.misc_expense_per_hour);
              return Number.isFinite(v) && v >= 0 ? v : 0;
            }
          } catch {
            /* ignore */
          }
          return 0;
        })();

        const [mappingRaw, billingRateRes, invoiceRaw, overridesRaw, rateConfigRaw, gradeTechRaw, miscHr] = await Promise.all([
          fetch('/ts_project_json/client_project/client_projects_mapping_info.json').then(r => r.ok ? r.json() : []),
          fetch('/json-data/client_billing_rate.json').then(r => r.ok ? r.json() : null),
          fetch('/client_invoice_by_period.json').then(r => r.ok ? r.json() : null),
          fetch('/config/account_owner_project_overrides.json').then(r => r.ok ? r.json() : { overrides: [] }),
          fetch('/config/rate_config.json').then(r => r.ok ? r.json() : null).catch(() => null),
          fetch('/resource_rate_config/grade_tech_permutations.json').then(r => r.ok ? r.json() : []).catch(() => []),
          miscPromise,
        ]);

        if (cancelled) return;

        // Prefer /json-data/ (CI/build output); repo often only has /data_models/ copy → would 404 and yield $0 revenue
        let billingRateRaw: Record<string, unknown> =
          billingRateRes && typeof billingRateRes === 'object' ? (billingRateRes as Record<string, unknown>) : {};
        const billingClientsFirst = Array.isArray(billingRateRaw.clients) ? billingRateRaw.clients : [];
        if (billingClientsFirst.length === 0) {
          const fb = await fetch('/data_models/client_billing_rate.json');
          if (fb.ok) billingRateRaw = await fb.json();
        }

        // Build projectToClient map. Some rows have client_id but blank client_name;
        // back-fill from any sibling row with the same client_id so drill-down client
        // rows show "3T TRANSFORM LIMITED" instead of "—".
        const mappingRows: any[] = Array.isArray(mappingRaw) ? mappingRaw : [];
        const clientIdToName: Record<string, string> = {};
        mappingRows.forEach((row: any) => {
          const cid = row?.client_id != null ? String(row.client_id).trim() : '';
          const nm = String(row?.client_name ?? '').trim();
          if (cid && nm) clientIdToName[cid] = nm;
        });
        const ptc: Record<string, { client_id: string; client_name: string }> = {};
        mappingRows.forEach((row: any) => {
          if (row?.project_id == null) return;
          const cid = String(row.client_id ?? '').trim();
          let client_name = String(row.client_name ?? '').trim();
          if (!client_name && cid && clientIdToName[cid]) {
            client_name = clientIdToName[cid];
          }
          // Same fallback as ExecutiveDashboard active-project path: never show blank
          // client label when we at least have a client_id.
          if (!client_name && cid) {
            client_name = `Client ${cid}`;
          }
          ptc[String(row.project_id)] = { client_id: cid, client_name };
        });

        // Build billing rate map: JSON is { clients: [{ client_id, client_billing_rate }] }
        const brMap: Record<string, number> = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const billingClients: any[] = Array.isArray(billingRateRaw.clients) ? billingRateRaw.clients : [];
        billingClients.forEach((c: any) => {
          const cid = c.client_id != null ? Number(c.client_id) : null;
          const rate = c.client_billing_rate != null ? Number(c.client_billing_rate) : null;
          if (cid != null && !isNaN(cid) && rate != null && !isNaN(rate)) {
            brMap[String(cid)] = rate;
          }
        });

        // Build grade-tech rate map from grade_tech_permutations.json: [{ grade_name, tech_name, price }]
        const gtMap: Record<string, number> = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (Array.isArray(gradeTechRaw) ? gradeTechRaw : []).forEach((p: any) => {
          const g = p.grade_name != null ? String(p.grade_name).trim() : '';
          const t = p.tech_name != null ? String(p.tech_name).trim() : '';
          if (g && t && p.price != null) gtMap[`${g}|${t}`] = Number(p.price);
        });

        const overrides: AccountOwnerOverride[] = (overridesRaw?.overrides ?? []);

        setRecords(allRecords);
        setAmManifest(ams);
        setProjectToClient(ptc);
        setClientBillingRateByClientId(brMap);
        setGradeTechRateByGradeTech(gtMap);
        setClientInvoiceByPeriod(invoiceRaw);
        setAccountOwnerProjectOverrides(overrides);
        setRateConfig(rateConfigRaw);
        setMiscExpensePerHour(miscHr);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return {
    records,
    amManifest,
    projectToClient,
    clientBillingRateByClientId,
    gradeTechRateByGradeTech,
    clientInvoiceByPeriod,
    accountOwnerProjectOverrides,
    rateConfig,
    miscExpensePerHour,
    loading,
    error,
    refetch,
  };
}
